import type { GateMetrics, Hint } from '../fsm/machine';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// I GATE DI QUALITÀ (ARCHITETTURA §6.1 F2, §6.5, ADR-029): le stesse quattro metriche che il server ricontrolla, calcolate
// sul "raster dei gate" = luminanza alla forma del fotogramma, lato lungo 400 px, media su area (vedi `raster.ts`). Le soglie
// sono DATI (GET /api/v1/config) e si tarano sui dati della campagna; qui c'è solo la formula.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface GateThresholds {
  minBlurVariance: number;
  maxClipLowShare: number;
  maxClipHighShare: number;
  maxGlareShare: number;
}

/** Stesse soglie di GateThresholds.Default sul server: valgono finché /api/v1/config non risponde. */
export const DEFAULT_THRESHOLDS: GateThresholds = { minBlurVariance: 100, maxClipLowShare: 0.05, maxClipHighShare: 0.02, maxGlareShare: 0.01 };

/** Luminanza Rec. 601 di un pixel, intera (troncata). Un'unica formula per il percorso dal vivo e per quello esatto. */
export function luma(r: number, g: number, b: number): number {
  return Math.floor((299 * r + 587 * g + 114 * b + 500) / 1000);
}

/** Luminanza Rec. 601 da RGBA: un byte per pixel. */
export function grayFromRgba(rgba: Uint8ClampedArray, widthPx: number, heightPx: number): Uint8Array {
  const n = widthPx * heightPx;
  const gray = new Uint8Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) {
    gray[i] = luma(rgba[j]!, rgba[j + 1]!, rgba[j + 2]!);
  }
  return gray;
}

export function computeGateMetrics(gray: Uint8Array, widthPx: number, heightPx: number): GateMetrics {
  const n = widthPx * heightPx;
  let low = 0;
  let high = 0;
  for (let i = 0; i < n; i++) {
    const v = gray[i]!;
    if (v <= 8) low++;
    if (v >= 247) high++;
  }

  // Mosso: varianza del laplaciano a 4 vicini sull'interno del raster.
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < heightPx - 1; y++) {
    const row = y * widthPx;
    for (let x = 1; x < widthPx - 1; x++) {
      const i = row + x;
      const l = 4 * gray[i]! - gray[i - 1]! - gray[i + 1]! - gray[i - widthPx]! - gray[i + widthPx]!;
      sum += l;
      sumSq += l * l;
      count++;
    }
  }
  const mean = count > 0 ? sum / count : 0;
  const blurVariance = count > 0 ? sumSq / count - mean * mean : 0;

  // Riflesso sul film: blocchi 16×16 saturi per almeno il 95 % (una macchia speculare, non un cartone chiaro).
  const block = 16;
  let glare = 0;
  for (let by = 0; by + block <= heightPx; by += block) {
    for (let bx = 0; bx + block <= widthPx; bx += block) {
      let sat = 0;
      for (let y = by; y < by + block; y++) {
        const row = y * widthPx;
        for (let x = bx; x < bx + block; x++) if (gray[row + x]! >= 250) sat++;
      }
      if (sat >= 0.95 * block * block) glare += block * block;
    }
  }

  return { blurVariance, clipLowShare: low / n, clipHighShare: high / n, glareShare: glare / n };
}

/**
 * Stessi codici, suggerimenti e ORDINE di GateFilter sul server: il rifiuto locale evita un upload, il server resta il giudice.
 * Il buio e il bruciato si controllano PRIMA della nitidezza: con un'immagine nera o bianca la varianza del laplaciano è zero
 * e «Mosso» sarebbe un messaggio falso (ADR-029). Il riflesso resta ultimo.
 */
export function evaluateGates(m: GateMetrics, t: GateThresholds): Hint | null {
  if (m.clipLowShare > t.maxClipLowShare) return { code: 'frame-dark', hint: 'Troppo scuro: più luce o spostati' };
  if (m.clipHighShare > t.maxClipHighShare) return { code: 'frame-overexposed', hint: 'Troppo chiaro: evita il controluce' };
  if (m.blurVariance < t.minBlurVariance) return { code: 'frame-blurred', hint: 'Mosso: tieni ferma la fotocamera' };
  if (m.glareShare > t.maxGlareShare) return { code: 'frame-glare', hint: 'Riflesso sul film: cambia angolo' };
  return null;
}
